import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_TOKEN } from 'constants/constants';
import {
  ICustomerObject,
  IRegistrationDbCustomer,
} from 'pages/Shared/DealerCustomers/Models';

const token = localStorage.getItem(API_TOKEN);

export const dealerCustomerApi = createApi({
  reducerPath: 'dealerCustomerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ACTIVITY_BASE_URL,
    prepareHeaders: (headers) => {
      const authToken = localStorage.getItem(API_TOKEN);
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Customers', 'OneCustomer', 'RegistrationDbCustomer'],
  keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
  endpoints: (builder) => ({
    getCustomerList: builder.query<ICustomerObject[], string>({
      query: (dealerId) => `/v1/dealer-customers?dealerId=${dealerId}`,
      providesTags: ['Customers'],
      // Add cache configuration
      keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    }),
    getOnlyOwnCustomerList: builder.query<ICustomerObject[], string>({
      query: (dealerId) =>
        `/v1/dealer-customers?dealerId=${dealerId}&showSubDealerCustomers=false`,
      providesTags: ['Customers'],
      // Add cache configuration
      keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    }),
    getOneCustomer: builder.query<ICustomerObject, number | string>({
      query: (id) => `/v1/dealer-customers/${id}`,
      providesTags: ['OneCustomer'],
    }),
    getRegistrationDbCustomerInfo: builder.query<
      IRegistrationDbCustomer,
      string
    >({
      query: (ticketId) => `v1/registration/db/customers/${ticketId}`,
      providesTags: ['RegistrationDbCustomer'],
    }),
    addNewCustomer: builder.mutation<[], ICustomerObject>({
      query: (data) => {
        const body = {
          dealerId: data.dealerInfo.dealerId,
          customerInfo: data.customerInfo,
        };
        return {
          url: '/v1/dealer-customers',
          method: 'Post',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body,
        };
      },
      invalidatesTags: ['Customers', 'OneCustomer'],
    }),
    updateCustomer: builder.mutation<[], ICustomerObject>({
      query: (data) => {
        return {
          url: `/v1/dealer-customers/${data.dealerCustomerId}`,
          method: 'Put',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: data,
        };
      },
      invalidatesTags: ['Customers', 'OneCustomer'],
    }),
    checkCustomerExists: builder.query<{ customerExists: boolean }, string>({
      query: (customerId) => `/v1/customers/is-unique/${customerId}`,
      // Add cache configuration
      keepUnusedDataFor: 60, // Keep unused data in cache for 60 seconds
    }),
  }),
});

export const {
  useGetCustomerListQuery,
  useGetOnlyOwnCustomerListQuery,
  useGetOneCustomerQuery,
  useGetRegistrationDbCustomerInfoQuery,
  useAddNewCustomerMutation,
  useUpdateCustomerMutation,
} = dealerCustomerApi;
